import {
  DevCallConversationsQuery,
  DevCallConversationsQueryVariables,
} from "@/graphql/generated/graphql";
import { DevCallConversations } from "@/graphql/queries/DevCallConversations";
import { useQueryWrapper } from "@/hooks/useQueryWrapper/useQueryWrapper";
import { type DataTableLoadingState } from "@aircall/ds";
import { NetworkStatus } from "@apollo/client";
import { useCallback } from "react";
import type { CallConversation } from "./table/graphql/types";

const DEV_CONVERSATIONS_LIMIT = 30;

// Dev-only helper backing the "/" landing page. Fetches recent call
// conversations (token-paginated, like conversation-center-ext) and feeds the
// ported CC table (mapToRows). Scrolling to the bottom loads the next page.
export function useDevConversations() {
  const { data, loading, error, fetchMore, networkStatus } = useQueryWrapper<
    DevCallConversationsQuery,
    DevCallConversationsQueryVariables
  >(DevCallConversations, {
    variables: { token: "1", limit: DEV_CONVERSATIONS_LIMIT },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.getCallConversations;
  const conversations = (page?.items ?? []).filter(
    (item): item is CallConversation => Boolean(item)
  );
  const total = page?.pageInfo?.total ?? conversations.length;
  const nextToken = page?.pageInfo?.nextToken ?? null;
  const hasMore = Boolean(nextToken);

  // Mirrors CC's DataTable phases from a single networkStatus value so they
  // can't disagree mid-transition: fetching a page -> "loadingMore" (spinner
  // row, rows stay), no data yet -> "loading" (skeletons), else -> "idle".
  const loadingState: DataTableLoadingState =
    networkStatus === NetworkStatus.fetchMore
      ? "loadingMore"
      : // On error with no data, drop to "idle" so the table renders its
        // (error) emptyState instead of skeleton rows forever.
        !data && !error
        ? "loading"
        : "idle";

  const loadMore = useCallback(() => {
    // Bail if there's no next page, or a page fetch is already in flight -
    // a second onFetchMore would reuse the same token and append duplicate rows.
    if (!nextToken || networkStatus === NetworkStatus.fetchMore) {
      return;
    }
    // assets-page has no cache field-policy merge for getCallConversations
    // (that lives in the production cache.config.ts and only matters here), so
    // append pages via updateQuery rather than touching shared cache config.
    void fetchMore({
      variables: { token: nextToken },
      updateQuery: (prev, { fetchMoreResult }) => {
        const nextPage = fetchMoreResult?.getCallConversations;
        if (!nextPage) {
          return prev;
        }
        return {
          getCallConversations: {
            ...nextPage,
            items: [
              ...(prev.getCallConversations?.items ?? []),
              ...(nextPage.items ?? []),
            ],
          },
        };
      },
    });
  }, [fetchMore, nextToken, networkStatus]);

  return {
    conversations,
    total,
    hasMore,
    loadingState,
    loadMore,
    loading,
    error,
  };
}
